"use client";

import { Button } from "@pex/ui-web";
import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";

const TESTIMONIALS = [
  {
    quote:
      "\u201CGave me a better understanding of effective recovery and how to grow in Recovery.\u201D",
    name: "Riaan Louw",
    avatar: "/images/testimonials/Countryside_Scenes_228_Farmhouse_ml5bij.webp",
  },
  {
    quote: "\u201CVery empowering, educational and thought provoking.\u201D",
    name: "Mandy Preston",
    avatar: "/images/testimonials/Flowers_440_Bouquet_of_daisies_lc7mqq.webp",
  },
  {
    quote:
      "\u201CLife changing! Great support structure as well as a very experienced team who are passionate about restoring people\u2019s lives and setting them free from addiction. Highly recommend!\u201D",
    name: "Ruan De Wet",
    avatar: "/images/testimonials/Beaches___Oceans_71_Fishing_rod_6eh5t5.webp",
  },
  {
    quote:
      "\u201CI found the help I needed before it was too late. Forever grateful to Project Exodus and the material they teach.\u201D",
    name: "Geraldine Cronje",
    avatar: "/images/testimonials/Countryside_Scenes_237_Fields_yebkqr.webp",
  },
  {
    quote:
      "\u201CI am lifted out of a terribly dark and desperate place, and no longer struggle for vacillating abstinence: I am blessed and SO grateful to be maintaining freedom!\u201D",
    name: "Ian Rhind",
    avatar: "/images/testimonials/Skylines_577_Christ_the_Redeemer_q4tugc.webp",
  },
  {
    quote:
      "\u201COpened my eyes to the complexity of addiction and helped my understanding.\u201D",
    name: "Ashlea Jorgensen",
    avatar:
      "/images/testimonials/Beaches___Oceans_25_Beach_hammock_4znuam.avif",
  },
  {
    quote:
      "\u201CIt\u2019s given me new hope and rekindled the passion to go after that \u2018one.\u2019\u201D",
    name: "Mark Govender",
    avatar:
      "/images/testimonials/Parks___Natural_Landmarks_607_Serengeti_National_Park_ku890d.webp",
  },
  {
    quote:
      "\u201CIn-depth insight on the recovery journey. Very practical. Evidence-based insights, shared stories, and Holy Spirit-filled. Impact of bringing change. This experience reduced the \u2018overwhelming feeling\u2019 (less intimidating) to tackle Recovery.\u201D",
    name: "CJ Philander",
    avatar:
      "/images/testimonials/Mountains_286_Rocky_Mountain_National_Park_vtnqkn.webp",
  },
  {
    quote:
      "\u201CThe difference is tremendous. Addiction doesn\u2019t have the hold on him that it did and there\u2019s a marked change in behavioural habits on both of our sides.\u201D",
    name: "Claire Barnett",
    avatar: "/images/testimonials/Flowers_463_Wedding_bouquet_fluowr.webp",
  },
] as const;

function TestimonialCard({
  quote,
  name,
  avatar,
}: {
  quote: string;
  name: string;
  avatar: string;
}) {
  return (
    <div className="rounded-sm border border-border/60 bg-card p-8">
      <p className="body-base">{quote}</p>
      <div className="mt-4 flex items-center gap-3">
        <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full">
          <Image
            alt=""
            className="object-cover"
            fill
            sizes="36px"
            src={avatar}
          />
        </div>
        <span className="font-semibold text-sm">{name}</span>
      </div>
    </div>
  );
}

export function CommunityVoices() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Both columns pre-shifted upward so neither edge (top or bottom) is ever visible.
  // col1 drifts further up; col2 drifts down toward its start position — opposite directions.
  // ~45% travel range (up from 33%) gives noticeably faster scroll, especially on mobile.
  const col1Y = useTransform(scrollYProgress, [0, 1], ["-5%", "-50%"]);
  const col2Y = useTransform(scrollYProgress, [0, 1], ["-50%", "-5%"]);

  const col1 = TESTIMONIALS.filter((_, i) => i % 2 === 0);
  const col2 = TESTIMONIALS.filter((_, i) => i % 2 !== 0);

  return (
    <section className="py-28" ref={sectionRef}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-0">
        {/* Fixed height box clips card columns — implies abundance of testimonials */}
        <div className="h-[65vh] overflow-hidden rounded-2xl border border-border px-6 md:h-[50vh] md:px-10">
          <div className="grid h-full gap-10 md:grid-cols-[1fr_2fr]">
            {/* Left — vertically centred heading + CTAs */}
            <div className="flex min-h-0 flex-col justify-center space-y-4 py-6 md:py-10">
              <h2 className="heading-2 font-bold leading-none">
                Community
                <br />
                Voices
              </h2>
              <p className="body-base text-muted-foreground">
                Hear from those whose lives have been changed through our
                recovery groups and programmes.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button
                  asChild
                  className="w-full sm:w-auto"
                  variant="secondary-hybrid"
                >
                  <Link href="/find-help">Learn More</Link>
                </Button>
                <Button
                  asChild
                  className="w-full sm:w-auto"
                  variant="link-icon"
                >
                  <Link href="/contact">
                    Contact
                    <svg
                      aria-hidden="true"
                      fill="none"
                      height="16"
                      viewBox="0 0 16 16"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 3L11 8L6 13"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right — parallax dual-column card grid, outer box clips flush */}
            <div className="min-h-0 overflow-hidden">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <motion.div
                  className="space-y-4 pb-40"
                  style={{ y: col1Y, willChange: "transform" }}
                >
                  {col1.map((t) => (
                    <TestimonialCard
                      avatar={t.avatar}
                      key={t.name}
                      name={t.name}
                      quote={t.quote}
                    />
                  ))}
                </motion.div>
                <motion.div
                  className="space-y-4 pb-40"
                  style={{ y: col2Y, willChange: "transform" }}
                >
                  {col2.map((t) => (
                    <TestimonialCard
                      avatar={t.avatar}
                      key={t.name}
                      name={t.name}
                      quote={t.quote}
                    />
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
